import React, { useState } from 'react';
import { X } from 'lucide-react';
import C from '../../constants/colors';

const CATEGORY_ORDER = ['Operations', 'Visitors', 'HSE', 'T&A', 'Admin'];

export default function ProfileEditorModal({ profile, allViews, onSave, onClose }) {
  const [name, setName]               = useState(profile?.name || '');
  const [description, setDescription] = useState(profile?.description || '');
  const [selected, setSelected]       = useState(new Set(profile?.viewKeys || []));
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    acc[cat] = allViews.filter(v => v.category === cat);
    return acc;
  }, {});

  async function handleSave() {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ name: name.trim(), description: description.trim(), viewKeys: [...selected] });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '12px', width: '520px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: `1px solid ${C.border}` }}>
          <span style={{ fontSize: '15px', fontWeight: 700, color: C.textPrimary }}>{profile ? 'Edit Profile' : 'New Profile'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '2px' }}><X size={16} /></button>
        </div>

        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '11px', color: C.textMuted, fontWeight: 600, display: 'block', marginBottom: '4px' }}>PROFILE NAME</label>
            <input
              value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Night Shift Operator"
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '7px', padding: '8px 10px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '11px', color: C.textMuted, fontWeight: 600, display: 'block', marginBottom: '4px' }}>DESCRIPTION (optional)</label>
            <input
              value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of this role's responsibilities"
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '7px', padding: '8px 10px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          {CATEGORY_ORDER.map(cat => {
            const views = grouped[cat];
            if (!views?.length) return null;
            return (
              <div key={cat} style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>{cat}</div>
                {views.map(v => (
                  <label key={v.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 0', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selected.has(v.key)}
                      onChange={e => {
                        const next = new Set(selected);
                        e.target.checked ? next.add(v.key) : next.delete(v.key);
                        setSelected(next);
                      }}
                      style={{ accentColor: C.blue, width: '14px', height: '14px' }}
                    />
                    <span style={{ fontSize: '13px', color: C.textPrimary }}>{v.label}</span>
                    {v.description && <span style={{ fontSize: '11px', color: C.textMuted }}>{v.description}</span>}
                  </label>
                ))}
              </div>
            );
          })}
        </div>

        {error && <div style={{ padding: '0 20px 8px', fontSize: '12px', color: C.red }}>{error}</div>}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', padding: '14px 20px', borderTop: `1px solid ${C.border}` }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: '7px', border: `1px solid ${C.border}`, background: 'none', color: C.textSecondary, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '8px 18px', borderRadius: '7px', border: 'none', background: C.blue, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit' }}>
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
        </div>
      </div>
    </div>
  );
}
